import { categories, videos } from "@/mocks/videos";
import type { Category, VideoItem } from "@/types/video";

export type SortKey = "latest" | "hottest";
export type DurationKey = "all" | "short" | "medium" | "long";

export const sortOptions: { value: SortKey; label: string }[] = [
  { value: "latest", label: "最新发布" },
  { value: "hottest", label: "最多播放" },
];

export const durationOptions: { value: DurationKey; label: string }[] = [
  { value: "all", label: "全部时长" },
  { value: "short", label: "1 分钟以内" },
  { value: "medium", label: "1-3 分钟" },
  { value: "long", label: "3 分钟以上" },
];

export function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

export function formatViews(views: number): string {
  if (views >= 10000) {
    const value = views / 10000;
    return `${value >= 100 ? Math.round(value) : value.toFixed(1)}万`;
  }
  return views.toString();
}

export function formatDate(date: string): string {
  return date.replace(/-/g, ".");
}

export function getCategory(categoryId: string): Category | undefined {
  return categories.find((item) => item.id === categoryId);
}

export function getCategoryName(categoryId: string): string {
  return getCategory(categoryId)?.name ?? "未分类";
}

export function getVideo(videoId: string): VideoItem | undefined {
  return videos.find((item) => item.id === videoId);
}

export function countByCategory(categoryId: string): number {
  return videos.filter((item) => item.categoryId === categoryId).length;
}

export function filterByDuration(list: VideoItem[], duration: DurationKey): VideoItem[] {
  if (duration === "short") return list.filter((item) => item.duration <= 60);
  if (duration === "medium") return list.filter((item) => item.duration > 60 && item.duration <= 180);
  if (duration === "long") return list.filter((item) => item.duration > 180);
  return list;
}

export function sortVideos(list: VideoItem[], sort: SortKey): VideoItem[] {
  const next = [...list];
  if (sort === "hottest") {
    return next.sort((a, b) => b.views - a.views);
  }
  return next.sort((a, b) => (a.uploadTime < b.uploadTime ? 1 : -1));
}

export function getCategoryVideos(
  categoryId: string,
  sort: SortKey = "latest",
  duration: DurationKey = "all"
): VideoItem[] {
  const list = videos.filter((item) => item.categoryId === categoryId);
  return sortVideos(filterByDuration(list, duration), sort);
}

export function searchVideos(keyword: string): VideoItem[] {
  const key = keyword.trim().toLowerCase();
  if (!key) return [];
  return videos.filter((item) => {
    const category = getCategoryName(item.categoryId);
    return (
      item.title.toLowerCase().includes(key) ||
      item.description.toLowerCase().includes(key) ||
      item.author.toLowerCase().includes(key) ||
      category.toLowerCase().includes(key) ||
      item.tags.some((tag) => tag.toLowerCase().includes(key))
    );
  });
}

export function getRelatedVideos(video: VideoItem, limit = 6): VideoItem[] {
  const scored = videos
    .filter((item) => item.id !== video.id)
    .map((item) => {
      let score = 0;
      if (item.categoryId === video.categoryId) score += 5;
      score += item.tags.filter((tag) => video.tags.includes(tag)).length * 3;
      score += item.views / 500000;
      return { item, score };
    })
    .sort((a, b) => b.score - a.score);

  return scored.slice(0, limit).map((entry) => entry.item);
}

export function getFeaturedVideos(): VideoItem[] {
  return videos.filter((item) => item.featured);
}

export function getTrendingVideos(limit = 5): VideoItem[] {
  return [...videos].sort((a, b) => b.views - a.views).slice(0, limit);
}

export function getLatestVideos(limit = 8): VideoItem[] {
  return sortVideos(videos, "latest").slice(0, limit);
}